import { forwardRef, useMemo } from "react";

import { buildFleetGeometry } from "../lib/geometry";
import type { TopologyPayload } from "../lib/types/topology";


interface CabinBarProps {
  topology: TopologyPayload;
  currentCabin?: string;
  /** Mobile pinch-zoom scale — letter positions track the zoomed SVG. */
  scale?: number;
  /** Left padding of the LOPA scroll pane (desktop p-4=16, mobile p-2=8). */
  padLeft?: number;
  /** Compact mobile styling: thinner bar, no sidebar spacer. */
  compact?: boolean;
  /** Width of the right-side spacer (panel + divider) — panel is resizable. */
  spacerW?: number;
  /** viewBoxX of the LIVE geometry (viewMode-dependent). Without it we
   *  recompute with the default view, which misaligns letters in MCU view. */
  viewBoxX?: number;
}

const SIDEBAR_W = 420;


const CabinBar = forwardRef<HTMLDivElement, CabinBarProps>(
  function CabinBar(
    { topology, currentCabin, scale = 1, padLeft = 16, compact = false,
      spacerW = SIDEBAR_W, viewBoxX: viewBoxXProp },
    ref,
  ) {
    const { layout } = topology;
    const cellW = layout.canvas.cell_w;
    const masterCols = layout.columns.master;

    const geometry = useMemo(
      () => buildFleetGeometry(topology),
      [topology],
    );
    const { cabinGrids } = geometry;
    const viewBoxX = viewBoxXProp ?? geometry.viewBoxX;

    const cabinLabel = currentCabin || Object.keys(layout.cabins)[0] || "Cabin";

    // Column letters + positions follow the CURRENT cabin's layout (2-4-2 vs
    // 3-3 etc.), matching what the SVG draws for that cabin — not the master
    // columns. Falls back to master when the cabin has no grid.
    const grid = cabinGrids.get(cabinLabel);
    const cabinLayoutCols =
      layout.cabins[cabinLabel]?.layout ?? masterCols;
    const seatLetters = cabinLayoutCols.filter((c) => c !== "|");
    const gridLeft = layout.canvas.left_pad + layout.canvas.cabin_col_offset;
    const colXMap: Record<string, number> = {};
    if (grid) {
      for (const letter of seatLetters) {
        if (grid.colX[letter] !== undefined) colXMap[letter] = grid.colX[letter];
      }
    } else {
      let x = gridLeft;
      for (const c of masterCols) {
        if (c === "|") x += layout.canvas.gap_aisle;
        else { colXMap[c] = x; x += cellW; }
      }
    }

    return (
      <div
        className="flex shrink-0 border-y border-slate-200 bg-slate-50/95 backdrop-blur"
        style={{ height: compact ? 24 : 28 }}
      >
        <div className="relative min-w-0 flex-1 overflow-hidden">
          {/* Translated by FleetPage on horizontal scroll. */}
          <div
            ref={ref}
            className="absolute inset-y-0 left-0"
            style={{ width: "max-content", willChange: "transform" }}
          >
            {seatLetters.map((letter) => {
              if (colXMap[letter] === undefined) return null;
              const coord = colXMap[letter] + cellW / 2;
              const pixelX = padLeft + (coord - viewBoxX) * scale;
              return (
                <span
                  key={letter}
                  className={
                    "absolute font-mono font-bold tracking-wider text-slate-700 " +
                    (compact ? "text-[10px]" : "text-xs")
                  }
                  style={{
                    left: pixelX,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                  }}
                >
                  {letter}
                </span>
              );
            })}
          </div>
          <div
            className={
              "absolute inset-y-0 left-0 z-10 flex items-center border-r border-slate-200 bg-slate-100 " +
              (compact ? "px-2.5" : "px-4")
            }
            style={{
              minWidth: compact ? 0 : 140,
              boxShadow: "6px 0 10px -4px rgba(15,23,42,0.08)",
            }}
          >
            <span
              className={
                "font-semibold uppercase tracking-wider text-slate-600 " +
                (compact ? "text-[10px]" : "text-xs")
              }
            >
              {cabinLabel}
            </span>
          </div>
        </div>
        {!compact && (
          <div
            className="border-l border-slate-200 bg-slate-100/60"
            style={{ width: spacerW, minWidth: spacerW }}
            aria-hidden
          />
        )}
      </div>
    );
  },
);

export default CabinBar;
